import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Fingerprint, LockKeyhole, Mail } from "lucide-react";
import { api } from "./api";
import { AuthScreenLayout, type CurrentUser, useAuth } from "./auth";
import { authenticateWithPasskey, passkeyErrorMessage } from "./passkeys";
import {
  APP_LOCK_TIMEOUT_MS,
  appLockRequired,
  clearAppLockRecord,
  mobileAppLockEnabled,
  readAppLockRecord,
  writeAppLockRecord,
} from "./app-lock-state";
const HEARTBEAT_MS = 15_000;

function SessionLockedScreen({ expectedUserId, expectedEmail, onUnlocked, unlockReady = true }: {
  expectedUserId: string;
  expectedEmail?: string;
  onUnlocked: () => void;
  unlockReady?: boolean;
}) {
  const { refresh, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      const authentication = await authenticateWithPasskey();
      if (authentication.userId !== expectedUserId) {
        await api("/auth/sessions", { method: "DELETE" }).catch(() => undefined);
        throw new Error("Use a biometria da conta que estava aberta.");
      }
      clearAppLockRecord();
      await refresh();
      onUnlocked();
    } catch (caught) {
      setError(passkeyErrorMessage(caught, "Não foi possível confirmar sua identidade."));
    } finally {
      setBusy(false);
    }
  };

  const usePassword = async () => {
    clearAppLockRecord();
    await logout();
  };

  return <AuthScreenLayout>
    <section className="login-form session-unlock-form" aria-labelledby="session-lock-title">
      <header className="login-form-header">
        <h1 id="session-lock-title">Bem-vindo de volta</h1>
      </header>
      <div className="login-fields session-unlock-fields" aria-label="Conta protegida">
        <div className="auth-field">
          <span className="session-credential-label">E-mail</span>
          <div className="auth-input-control session-credential-value" aria-readonly="true">
            <Mail size={18} />
            <span>{expectedEmail || "Conta protegida"}</span>
          </div>
        </div>
        <div className="auth-field">
          <span className="session-credential-label">Senha</span>
          <div className="auth-input-control session-credential-value" aria-readonly="true">
            <LockKeyhole size={18} />
            <span aria-label="Senha preenchida">••••••••••••</span>
          </div>
        </div>
      </div>
      <div className="session-unlock-actions">
        {error && <p className="form-error" role="alert">{error}</p>}
        <button type="button" className="site-button site-button-red login-submit" onClick={() => void unlock()} disabled={busy || !unlockReady} aria-busy={busy || !unlockReady}>
          <Fingerprint size={18} />{!unlockReady ? "Protegendo…" : busy ? "Confirmando…" : "Continuar com biometria"}
        </button>
        <button type="button" className="login-link" onClick={() => void usePassword()} disabled={busy || !unlockReady}>Entrar com senha</button>
      </div>
    </section>
  </AuthScreenLayout>;
}

export function MobileSessionLock({ user, children }: { user: CurrentUser; children: ReactNode }) {
  const { logout } = useAuth();
  const enabled = useMemo(() => mobileAppLockEnabled(), []);
  const initialRecord = useMemo(() => readAppLockRecord(), []);
  const [locked, setLocked] = useState(() => enabled && appLockRequired(initialRecord, user.userId));
  const [unlockReady, setUnlockReady] = useState(() => !(enabled && appLockRequired(initialRecord, user.userId)));
  const locking = useRef(false);
  const hiddenAt = useRef<number | null>(null);

  const lock = useCallback(async () => {
    if (!enabled || locking.current) return;
    locking.current = true;
    setUnlockReady(false);
    writeAppLockRecord({
      userId: user.userId,
      accountEmail: user.email,
      lastActiveAt: Date.now(),
      locked: true,
      requireOnLaunch: true,
    });
    setLocked(true);
    await logout({ preserveAppLock: true, allSessions: true }).catch(() => undefined);
    setUnlockReady(true);
  }, [enabled, logout, user.email, user.userId]);

  useEffect(() => {
    if (!enabled) return;
    if (locked) {
      void lock();
      return;
    }

    const active = () => writeAppLockRecord({
      userId: user.userId,
      accountEmail: user.email,
      lastActiveAt: Date.now(),
      locked: false,
      requireOnLaunch: false,
    });
    const closing = () => writeAppLockRecord({
      userId: user.userId,
      accountEmail: user.email,
      lastActiveAt: Date.now(),
      locked: false,
      requireOnLaunch: true,
    });
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt.current = Date.now();
        closing();
        return;
      }
      const record = readAppLockRecord();
      const sameRunningInstance = hiddenAt.current !== null;
      const elapsed = sameRunningInstance ? Date.now() - hiddenAt.current! : Date.now() - (record?.lastActiveAt ?? Date.now());
      hiddenAt.current = null;
      if ((!sameRunningInstance && record?.requireOnLaunch) || elapsed >= APP_LOCK_TIMEOUT_MS) void lock();
      else active();
    };

    active();
    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === "visible") active();
    }, HEARTBEAT_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", closing);
    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", closing);
    };
  }, [enabled, lock, locked, user.email, user.userId]);

  if (!enabled) return children;
  if (locked) return <SessionLockedScreen expectedUserId={user.userId} expectedEmail={user.email} unlockReady={unlockReady} onUnlocked={() => setLocked(false)} />;
  return children;
}

export function StoredSessionLock({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const record = readAppLockRecord();
  if (!mobileAppLockEnabled() || !record?.locked || user) return children;
  return <SessionLockedScreen expectedUserId={record.userId} expectedEmail={record.accountEmail} onUnlocked={() => undefined} />;
}
